// src/hooks/useReplayTrackWidth.js
//
// Per-user width (in px) of the Live Map replay track line — the dimmed
// transparent backdrop polyline + its opaque traveled portion + the
// animated direction-flow dashes all scale to this value.
//
// State lives at MODULE scope so the two consumers stay in sync:
//   - ReplayTripPlayer (the gear stepper that SETS the value), and
//   - MiniReplayLayer  (the map layer that READS it to paint line-width).
// Per-instance useState would only re-render the setter's component, so
// dragging the stepper would not repaint the line until the next remount.
// Same convention as useTelemetryPrefs / positionAnimator / snap-trails.

import { useCallback, useEffect, useReducer } from 'react';

const LS_KEY = 'trackizy_livemap_replay_track_width';

const DEFAULT_WIDTH = 6;
const MIN_WIDTH = 2;
const MAX_WIDTH = 12;

function clampWidth(n) {
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(n)));
}

function readWidth() {
  try {
    const v = Number(localStorage.getItem(LS_KEY));
    return Number.isFinite(v) && v > 0 ? clampWidth(v) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

// Module-scope mutable state — shared by every hook instance.
const state = { width: readWidth() };

const listeners = new Set();
function notify() {
  listeners.forEach((l) => l());
}

// Cross-tab updates — storage event fires on OTHER tabs only.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === LS_KEY) { state.width = readWidth(); notify(); }
  });
}

export default function useReplayTrackWidth() {
  const [, forceUpdate] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    const sub = () => forceUpdate();
    listeners.add(sub);
    return () => { listeners.delete(sub); };
  }, []);

  const setWidth = useCallback((px) => {
    state.width = clampWidth(Number(px));
    try { localStorage.setItem(LS_KEY, String(state.width)); } catch { /* private-mode safari etc. */ }
    notify();
  }, []);

  return {
    width: state.width,
    setWidth,
    bounds: { min: MIN_WIDTH, max: MAX_WIDTH, defaultValue: DEFAULT_WIDTH },
  };
}
